/**
 * Deliberate-practice (刻意练习) print — separate from RevisionPrintAction.
 * Do NOT change RevisionPrintAction / RevisionPrintPreviewModal layout for this stream.
 *
 * Rules:
 * - No QR codes
 * - 5 questions per A4 page
 * - Compact drill worksheet layout
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { renderContentBodyHtml } from '../../../utils/renderRichTextHtml';
import {
  CONTENT_FONT_SIZE_PT,
  CONTENT_LINE_HEIGHT_RATIO,
  CONTENT_FONT_FAMILY_CSS,
  buildContentDocumentFontLinksHtml,
} from '../../../theme/contentTypography';
import {
  REVISION_PRINT_A4_HEIGHT_PX,
  REVISION_PRINT_A4_WIDTH_PX,
  getRevisionPrintEmbeddedImageMetrics,
  resolveRevisionPrintParticlesEmbeddedImages,
  resolveRevisionPrintPreviewScale,
  shortRevisionId,
  type RevisionPrintEmbeddedImage,
  type RevisionPrintParticle,
} from './RevisionPrintAction';
import {
  REVISION_PRINT_ORIGIN_SPINE_CSS,
  buildRevisionPrintSpineRowMarkup,
} from './printParticleOrigin';
import { downloadHtml, presentSystemPrintFromHtml } from './presentSystemPrint';

export const PAS_DP_PRINT_PARTICLES_PER_PAGE = 5;

const KATEX_VERSION = '0.16.25';
const EMBED_MAX_WIDTH = 240;
const EMBED_MAX_HEIGHT = 120;
const EMPTY_PRINT_PARTICLES_REF: RevisionPrintParticle[] = [];

type UsePasDeliberatePracticePrintActionOptions = {
  particles: RevisionPrintParticle[];
  sessionId?: string | null;
  onNotice?: (message: string) => void;
};

function escapeHtml(input: string) {
  return String(input || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeImageSrcAttribute(url: string) {
  const normalized = String(url || '').trim();
  if (!normalized || normalized.startsWith('data:image/')) {
    return normalized;
  }
  return escapeHtml(normalized);
}

function renderMathMarkup(input: string) {
  return renderContentBodyHtml(input);
}

function createPrintSessionId() {
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `ps_dp_${Date.now()}_${randomPart}`;
}

function looksLikeInlineImage(url?: string | null) {
  const normalized = String(url || '').trim();
  if (!normalized) {
    return false;
  }
  return normalized.startsWith('data:image/')
    || normalized.startsWith('https://')
    || normalized.startsWith('http://');
}

export function filterPasDeliberatePracticePrintParticles(particles: RevisionPrintParticle[]) {
  return particles.filter((particle) => {
    const question = String(particle.questionMain || particle.title || '').trim();
    return Boolean(question);
  });
}

export function chunkPasDeliberatePracticePrintParticles(particles: RevisionPrintParticle[]) {
  const pages: RevisionPrintParticle[][] = [];
  for (let index = 0; index < particles.length; index += PAS_DP_PRINT_PARTICLES_PER_PAGE) {
    pages.push(particles.slice(index, index + PAS_DP_PRINT_PARTICLES_PER_PAGE));
  }
  return pages;
}

function buildEmbeddedImageMarkup(image: RevisionPrintEmbeddedImage) {
  const metrics = getRevisionPrintEmbeddedImageMetrics(image, EMBED_MAX_WIDTH, EMBED_MAX_HEIGHT);
  if (image.renderMode === 'oss-crop') {
    return `
      <div class="dp-embed" style="width:${metrics.containerWidth}px;height:${metrics.containerHeight}px;">
        <img src="${escapeImageSrcAttribute(image.imageUrl)}" alt="" style="width:100%;height:100%;object-fit:fill;" />
      </div>
    `;
  }
  return `
    <div class="dp-embed" style="width:${metrics.containerWidth}px;height:${metrics.containerHeight}px;">
      <img
        src="${escapeImageSrcAttribute(image.imageUrl)}"
        alt=""
        style="width:${metrics.renderedImageWidth}px;height:${metrics.renderedImageHeight}px;left:${metrics.offsetLeft}px;top:${metrics.offsetTop}px;position:absolute;max-width:none;"
      />
    </div>
  `;
}

export function buildPasDeliberatePracticePrintHtml(params: {
  particles: RevisionPrintParticle[];
  sessionId?: string | null;
  printSessionId: string;
  previewViewportWidth?: number;
  /** When rendering a single preview page chunk, offset global question numbers. */
  particleIndexOffset?: number;
  /** When rendering a single preview page chunk, show this as 1-based page number. */
  pageNumber?: number;
}) {
  const printableParticles = filterPasDeliberatePracticePrintParticles(params.particles);
  const pages = chunkPasDeliberatePracticePrintParticles(printableParticles);
  const printedAt = new Date().toLocaleDateString('zh-CN');
  const previewScale = params.previewViewportWidth
    ? resolveRevisionPrintPreviewScale(params.previewViewportWidth)
    : null;
  const previewFrameWidth = previewScale
    ? Math.round(REVISION_PRINT_A4_WIDTH_PX * previewScale)
    : null;
  const previewFrameHeight = previewScale
    ? Math.round(REVISION_PRINT_A4_HEIGHT_PX * previewScale)
    : null;
  const sessionShortId = shortRevisionId(params.sessionId);
  const printShortId = shortRevisionId(params.printSessionId);
  const particleIndexOffset = Math.max(0, Math.floor(params.particleIndexOffset || 0));

  const pagesMarkup = pages.map((page, pageIndex) => {
    const displayPageNumber = params.pageNumber ?? (pageIndex + 1);
    const particlesMarkup = page.map((particle, particleIndex) => {
      const globalIndex = particleIndexOffset + pageIndex * PAS_DP_PRINT_PARTICLES_PER_PAGE + particleIndex + 1;
      const questionText = particle.questionMain || particle.title || '未写入题面';
      const embeddedImage = particle.embeddedImage && looksLikeInlineImage(particle.embeddedImage.imageUrl)
        ? particle.embeddedImage
        : null;
      const embedMarkup = embeddedImage ? buildEmbeddedImageMarkup(embeddedImage) : '';

      return `
        <section class="dp-item">
          <div class="dp-item-head">
            <span class="dp-index">${globalIndex}</span>
            <span class="dp-id">ID ${escapeHtml(shortRevisionId(particle.id))}</span>
          </div>
          ${buildRevisionPrintSpineRowMarkup({
            sourceOrigin: particle.sourceOrigin,
            spineLabel: particle.spineLabel,
            rowClass: 'dp-spine-row',
            spineClass: 'dp-spine',
            escapeHtml,
          })}
          <div class="dp-question">${renderMathMarkup(questionText)}</div>
          ${embedMarkup}
          <div class="dp-answer-space" aria-hidden="true"></div>
        </section>
      `;
    }).join('');

    // Pad empty slots so 5-row rhythm stays stable on partial last pages.
    const padCount = Math.max(0, PAS_DP_PRINT_PARTICLES_PER_PAGE - page.length);
    const padMarkup = Array.from({ length: padCount }, () => '<section class="dp-item dp-item-pad"></section>').join('');

    const pageMarkup = `
      <article class="print-page">
        <header class="page-header">
          <span>刻意练习 · 第 ${displayPageNumber} 页 · 每页 ${PAS_DP_PRINT_PARTICLES_PER_PAGE} 题</span>
          <span>${escapeHtml([sessionShortId && `SID ${sessionShortId}`, printShortId && `PID ${printShortId}`, printedAt].filter(Boolean).join(' · '))}</span>
        </header>
        <main class="page-body">${particlesMarkup}${padMarkup}</main>
      </article>
    `;

    if (previewScale && previewFrameWidth && previewFrameHeight) {
      return `
        <div class="preview-page-frame" style="width:${previewFrameWidth}px;height:${previewFrameHeight}px;">
          <div class="preview-page-inner" style="transform:scale(${previewScale});">
            ${pageMarkup}
          </div>
        </div>
      `;
    }

    return pageMarkup;
  }).join('');

  const bodyMarkup = previewScale
    ? `<div class="preview-root">${pagesMarkup}</div>`
    : pagesMarkup;

  const previewStyles = previewScale
    ? `
      body.preview-mode {
        background: #eef2f6;
        height: auto;
        min-height: 100%;
        padding: 0;
      }
      .preview-root {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0;
      }
      .preview-page-frame {
        overflow: hidden;
        border-radius: 8px;
        background: #ffffff;
        box-shadow: 0 8px 28px rgba(15, 23, 42, 0.12);
        flex: none;
      }
      .preview-page-inner {
        width: ${REVISION_PRINT_A4_WIDTH_PX}px;
        height: ${REVISION_PRINT_A4_HEIGHT_PX}px;
        transform-origin: top left;
      }
      body.preview-mode .print-page {
        width: ${REVISION_PRINT_A4_WIDTH_PX}px;
        height: ${REVISION_PRINT_A4_HEIGHT_PX}px;
        page-break-after: auto;
        break-after: auto;
      }
    `
    : '';

  return `<!DOCTYPE html>
<html lang="zh-CN">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    ${buildContentDocumentFontLinksHtml()}
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@${KATEX_VERSION}/dist/katex.min.css" />
    <style>
      @page { size: A4 portrait; margin: 0; }
      * { box-sizing: border-box; }
      html, body, article, header, main, section {
        margin: 0;
        padding: 0;
      }
      html, body { height: 100%; }
      body {
        color: #0f172a;
        background: #ffffff;
        font-family: ${CONTENT_FONT_FAMILY_CSS};
        font-size: ${CONTENT_FONT_SIZE_PT}pt;
        line-height: ${CONTENT_LINE_HEIGHT_RATIO};
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        width: 100%;
      }
      .print-page {
        padding: 10mm 14mm 12mm;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        page-break-after: always;
        break-after: page;
      }
      .print-page:last-child {
        page-break-after: auto;
        break-after: auto;
      }
      .page-header {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        color: #64748b;
        font-size: 10px;
        font-weight: 600;
        margin-bottom: 8px;
        flex: none;
      }
      .page-body {
        display: grid;
        grid-template-rows: repeat(${PAS_DP_PRINT_PARTICLES_PER_PAGE}, minmax(0, 1fr));
        gap: 6px;
        flex: 1;
        min-height: 0;
        overflow: hidden;
      }
      .dp-item {
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        padding: 8px 10px 10px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        min-height: 0;
        overflow: hidden;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .dp-item-pad {
        border-style: dashed;
        border-color: #f1f5f9;
        background: #fafbfc;
      }
      .dp-item-head {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: none;
      }
      .dp-index {
        width: 20px;
        height: 20px;
        border-radius: 999px;
        background: #ecfdf5;
        color: #0f766e;
        font-size: 11px;
        font-weight: 800;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .dp-id {
        color: #94a3b8;
        font-size: 9px;
        font-weight: 700;
      }
      ${REVISION_PRINT_ORIGIN_SPINE_CSS}
      .dp-question {
        color: #0f172a;
        font-weight: 700;
        font-size: ${CONTENT_FONT_SIZE_PT}pt;
        line-height: ${CONTENT_LINE_HEIGHT_RATIO};
        flex: none;
        overflow: hidden;
      }
      .dp-embed {
        position: relative;
        overflow: hidden;
        border-radius: 6px;
        background: #f8fafc;
        flex: none;
        max-width: 100%;
      }
      .dp-answer-space {
        flex: 1;
        min-height: 28px;
        border-top: 1px dashed #cbd5e1;
        margin-top: 2px;
      }
      .dp-question > :first-child { margin-top: 0; }
      .dp-question > :last-child { margin-bottom: 0; }
      .dp-question p,
      .dp-question ul,
      .dp-question ol {
        margin: 0.12em 0;
      }
      .text-run { white-space: pre-wrap; }
      .math-display { margin: 0.2em 0; max-width: 100%; }
      .katex { font-size: 1.02em; max-width: 100%; }
      .katex-display {
        display: block;
        text-align: center;
        margin: 0.2em 0;
        max-width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
      }
      ${previewStyles}
    </style>
  </head>
  <body class="${previewScale ? 'preview-mode' : ''}">${bodyMarkup}</body>
</html>`;
}

export function usePasDeliberatePracticePrintAction({
  particles,
  sessionId,
  onNotice,
}: UsePasDeliberatePracticePrintActionOptions) {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [resolvedParticles, setResolvedParticles] = useState(particles);
  const [resolvingEmbeddedImages, setResolvingEmbeddedImages] = useState(false);
  const printSessionId = useMemo(createPrintSessionId, []);
  const printJobGenerationRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    if (!particles.length) {
      setResolvedParticles((current) => (current.length === 0 ? current : EMPTY_PRINT_PARTICLES_REF));
      setResolvingEmbeddedImages(false);
      return () => {
        cancelled = true;
      };
    }
    setResolvedParticles(particles);
    const needsEmbed = particles.some((particle) => Boolean(particle.embeddedImageRecord));
    if (!needsEmbed) {
      setResolvingEmbeddedImages(false);
      return () => {
        cancelled = true;
      };
    }
    setResolvingEmbeddedImages(true);
    void resolveRevisionPrintParticlesEmbeddedImages(particles, { preferInline: false }).then((next) => {
      if (!cancelled) {
        setResolvedParticles(next);
        setResolvingEmbeddedImages(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [particles]);

  const printableParticles = useMemo(
    () => filterPasDeliberatePracticePrintParticles(resolvedParticles),
    [resolvedParticles],
  );

  const buildPrintHtml = useCallback(async () => {
    const withInlineImages = await resolveRevisionPrintParticlesEmbeddedImages(
      printableParticles.map((particle) => ({ ...particle, embeddedImage: null })),
      { preferInline: true },
    );
    setResolvedParticles(withInlineImages);
    return buildPasDeliberatePracticePrintHtml({
      particles: filterPasDeliberatePracticePrintParticles(withInlineImages),
      sessionId,
      printSessionId,
    });
  }, [printableParticles, printSessionId, sessionId]);

  const openPreview = useCallback(() => {
    if (!particles.length) {
      onNotice?.('暂无可打印的刻意练习题目。');
      return;
    }
    setPreviewVisible(true);
  }, [onNotice, particles.length]);

  const closePreview = useCallback(() => {
    printJobGenerationRef.current += 1;
    setPrinting(false);
    setSharing(false);
    setPreviewVisible(false);
  }, []);

  const print = useCallback(async () => {
    if (resolvingEmbeddedImages) {
      onNotice?.('嵌入图片正在准备中，请稍候。');
      return;
    }
    if (!printableParticles.length) {
      onNotice?.('暂无可打印的刻意练习题目。');
      return;
    }
    const jobId = printJobGenerationRef.current + 1;
    printJobGenerationRef.current = jobId;
    setPrinting(true);
    try {
      const html = await buildPrintHtml();
      if (jobId !== printJobGenerationRef.current) {
        return;
      }
      const outcome = await presentSystemPrintFromHtml({
        html,
        shareFallbackDialogTitle: '分享刻意练习打印 PDF',
        hidePreview: () => setPreviewVisible(false),
        isAborted: () => jobId !== printJobGenerationRef.current,
      });
      if (jobId !== printJobGenerationRef.current) {
        return;
      }
      if (outcome === 'printed') {
        onNotice?.('已打开系统打印面板。');
      } else if (outcome === 'shared-print-service') {
        onNotice?.('请选择卓易通或其他应用完成打印。');
      } else {
        onNotice?.('系统打印不可用，已生成打印稿下载。');
      }
    } catch (printError) {
      if (jobId !== printJobGenerationRef.current) {
        return;
      }
      const message = printError instanceof Error ? printError.message : '无法生成刻意练习打印材料';
      onNotice?.(`打印失败：${message}`);
      window.alert(`打印失败\n${message}`);
    } finally {
      if (jobId === printJobGenerationRef.current) {
        setPrinting(false);
      }
    }
  }, [buildPrintHtml, onNotice, printableParticles.length, resolvingEmbeddedImages]);

  const share = useCallback(async () => {
    if (resolvingEmbeddedImages) {
      onNotice?.('嵌入图片正在准备中，请稍候。');
      return;
    }
    if (!printableParticles.length) {
      onNotice?.('暂无可打印的刻意练习题目。');
      return;
    }
    const jobId = printJobGenerationRef.current + 1;
    printJobGenerationRef.current = jobId;
    setSharing(true);
    try {
      const html = await buildPrintHtml();
      if (jobId !== printJobGenerationRef.current) {
        return;
      }
      downloadHtml(html, `刻意练习-${shortRevisionId(printSessionId) || 'print'}.html`);
      if (jobId !== printJobGenerationRef.current) {
        return;
      }
      onNotice?.('已下载刻意练习打印稿（HTML）。可在浏览器中打开后「打印 → 存储为 PDF」。');
    } catch (shareError) {
      if (jobId !== printJobGenerationRef.current) {
        return;
      }
      const message = shareError instanceof Error ? shareError.message : '无法生成刻意练习打印材料';
      onNotice?.(`生成 PDF 失败：${message}`);
      window.alert(`生成 PDF 失败\n${message}`);
    } finally {
      if (jobId === printJobGenerationRef.current) {
        setSharing(false);
      }
    }
  }, [buildPrintHtml, onNotice, printableParticles.length, printSessionId, resolvingEmbeddedImages]);

  return {
    openPreview,
    closePreview,
    previewVisible,
    print,
    share,
    printing,
    sharing,
    printSessionId,
    resolvedParticles,
    resolvingEmbeddedImages,
  };
}
